import { useState, useEffect } from 'react'

export type TProcedureFilters = {
  rooms: string[]
  locations: string[]
  surgeonName: string | null
  surgeryName: string | null
  patientStatus: string | null
}

export type TUseFiltersReturnType = [
  filters: TProcedureFilters,
  setFilters: (filters: TProcedureFilters) => void,
  clearFilters: () => void,
  isAnyFilterActive: boolean
]

const FILTER_STORAGE_KEY = 'procedure-filters'

const initialFilters: TProcedureFilters = {
  rooms: [],
  locations: [],
  surgeonName: null,
  surgeryName: null,
  patientStatus: null,
}

function isProcedureFilters(value: unknown): value is TProcedureFilters {
  if (!value || typeof value !== 'object') return false

  const filters = value as Record<string, unknown>

  return (
    Array.isArray(filters.locations) &&
    filters.locations.every((item) => typeof item === 'string') &&
    Array.isArray(filters.rooms) &&
    filters.rooms.every((item) => typeof item === 'string') &&
    (filters.surgeonName === null || typeof filters.surgeonName === 'string') &&
    (filters.surgeryName === null || typeof filters.surgeryName === 'string') &&
    (filters.patientStatus === null ||
      typeof filters.patientStatus === 'string')
  )
}

function parseStoredFilters(storedFilters: string): TProcedureFilters {
  try {
    const parsed = JSON.parse(storedFilters)

    if (isProcedureFilters(parsed)) {
      return parsed
    }

    return initialFilters
  } catch (error) {
    return initialFilters
  }
}

export const useFilters = (): TUseFiltersReturnType => {
  const [filters, setFiltersState] = useState<TProcedureFilters>(initialFilters)
  const [isAnyFilterActive, setIsAnyFilterActive] = useState(false)

  useEffect(() => {
    const storedFilters = localStorage.getItem(FILTER_STORAGE_KEY)
    if (storedFilters) {
      try {
        const parsedFilters = parseStoredFilters(storedFilters)
        setFiltersState(parsedFilters)

        const hasActiveFilters = checkIfAnyFilterActive(parsedFilters)
        setIsAnyFilterActive(hasActiveFilters)
      } catch (error) {
        console.error('Failed to parse stored filters', error)
        localStorage.removeItem(FILTER_STORAGE_KEY)
      }
    }
  }, [])

  const checkIfAnyFilterActive = (filterState: TProcedureFilters): boolean => {
    return (
      (Array.isArray(filterState.locations) &&
        filterState.locations.length > 0) ||
      (Array.isArray(filterState.rooms) && filterState.rooms.length > 0) ||
      !!filterState.surgeonName ||
      !!filterState.surgeryName ||
      !!filterState.patientStatus
    )
  }

  const setFilters = (newFilters: TProcedureFilters) => {
    setFiltersState(newFilters)
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(newFilters))

    const hasActiveFilters = checkIfAnyFilterActive(newFilters)
    setIsAnyFilterActive(hasActiveFilters)
  }

  const clearFilters = () => {
    setFiltersState(initialFilters)
    localStorage.removeItem(FILTER_STORAGE_KEY)
    setIsAnyFilterActive(false)
  }

  return [filters, setFilters, clearFilters, isAnyFilterActive]
}
